import { Link } from "react-router-dom";
import {
  renderCustomColumnDefs,
  renderValidations,
} from "../../../shared/Table/table.utils";
import {
  capitalizeFirstLetter,
  formatDate,
  renderAmount,
} from "../../../utils";
import _ from "lodash";
import Badge from "../../../shared/Badge";
import Popover from "../../../shared/Popover";
import { format } from "date-fns";
import { getAppType, hasStoredPermission } from "../../../utils/authority";
import { PERMISSIONS } from "../../../constants/access-control";
import { JobTagsInline, StatusBadge } from "../../../components";

const canEditJobs = () => hasStoredPermission(PERMISSIONS.JOBS_EDIT);
const canAssignJobs = () => hasStoredPermission(PERMISSIONS.JOBS_ASSIGN);

// Config for table and form
const tableConfig = [
  {
    accessorKey: "loadNumber",
    // filterFn: (row, id, value) => {
    //   return value.includes(row.getValue(id));
    // },
    accessorFn: (e) => e.jobId || e.loadNumber,
    id: "jobId",
    visible: true,
    required: true,
    minWidth: 250,
    editable: canEditJobs(),
    cell: ({ getValue, row: { original }, table }) => {
      const listSearch = table.options.meta?.jobListSearch || "";
      return (
        <Link
          className="text-primary-600 hover:underline"
          to={`/jobs/${original._id}${listSearch}`}
        >
          {getValue() || original.loadNumber}
        </Link>
      );
    },
  },
  {
    id: "status",
    // editable: {
    //   type: "select",
    //   options: jobStatusOptions,
    // },
    cell: (info) => {
      const value = info.getValue();

      const appType = getAppType();

      const renderApproval = () => {
        if (appType === "serviceRepair") {
          const row = info.row.original.approval
            ? info.row.original.approval
            : null;
          if (!value || typeof value !== "string") return null;

          let color = "gray";
          const status = row?.status || null;

          if (status === "pending") {
            return null;
          }

          switch (status) {
            case "approved":
              color = "green";
              break;
            case "pending":
              color = "yellow";
              break;
            case "rejected":
              color = "red";
              break;
            default:
              color = "gray";
          }

          return (
            <Badge
              color={status ? color : "null"}
              label={capitalizeFirstLetter(status ? status : null)}
            />
          );
        }
        return null;
      };

      return (
        <>
          <StatusBadge className="mr-1" status={value} component="job" />
          {renderApproval()}
        </>
      );
    },
  },
  {
    id: "tags",
    header: "Tags",
    minWidth: 220,
    cell: ({ row: { original } }) => (
      <div onClick={(event) => event.stopPropagation()}>
        <JobTagsInline
          job={original}
          editable={canEditJobs() || canAssignJobs()}
          summarizeOverflow
        />
      </div>
    ),
  },

  {
    id: "appointmentDate",
    enableGlobalFilter: true,
    // accessorFn: (row) => formatDate(row.transactionDate),
    cell: (info) => {
      const value = info.getValue();
      return value && formatDate(value, null, true);
    },
    width: 180,
    visible: true,
  },
  {
    id: "assignedTo",
    header: "Assigned To",
    enableGlobalFilter: true,
    visible: true,
    cell: ({ getValue }) => {
      const assignedUsers = getValue();
      if (!assignedUsers || !Array.isArray(assignedUsers)) return null;

      const users = assignedUsers
        .filter((user) => user?.user?.fullName || user?.fullName)
        .map((user) => user?.user?.fullName || user?.fullName);

      if (users.length === 0) return null;

      // If only one user, show them directly
      if (users.length === 1) {
        return <Badge color="blue" label={users[0]} />;
      }

      // If multiple users, show first user and a count badge with popover
      return (
        <div className="flex items-center gap-2">
          <Badge color="blue" label={users[0]} />
          <Popover
            position="top"
            trigger={
              <Badge
                color="gray"
                label={`+${users.length - 1}`}
                className="cursor-pointer hover:bg-gray-200"
              />
            }
          >
            <div className="p-2 space-y-1">
              {users.slice(1).map((name, idx) => (
                <div key={idx} className="whitespace-nowrap">
                  {name}
                </div>
              ))}
            </div>
          </Popover>
        </div>
      );
    },
    minWidth: 140,
  },
  {
    id: "client",
    enableGlobalFilter: true,
    visible: true,
    editable: false,
    cell: ({ getValue }) => {
      return getValue()?.name;
    },
    minWidth: 140,
  },
  {
    id: "items",
    header: "Services",
    cell: (info) => info.getValue()?.length,
    width: 100,
    required: true,
    custom: {
      isValid: (value, obj) => {
        if (!value) return;
        const type = "leg";
        let errors = {};
        let isValid = true;
        const map = Object.keys(value).map((key) => value[key]);
        map.forEach((item, i) => {
          const id = type + "_" + (i + 1);
          const orderNumber = id + "_orderNumber";

          Object.keys(item).forEach((key) => {
            let renderOrderNumberError = obj.hasBeenInit[orderNumber];

            if (obj.valueIsEmpty(renderOrderNumberError)) {
              isValid = false;
              return;
            }
            if (!item[orderNumber]) {
              errors[orderNumber] = "This field is required";
              isValid = false;
            }
          });
        });

        return { isValid, errors };
      },
    },
  },
  {
    id: "totalCharges",
    accessorFn: (row) => {
      return row.calculatedTotalCharges || row.totalCharges;
    },
    cell: ({ getValue }) => renderAmount(getValue()),
    minWidth: 140,
    remove: !canEditJobs(),
  },
  {
    header: "Invoice",
    id: "invoiceId",
    minWidth: 200,
    cell: ({ getValue }) => {
      if (getValue()) {
        return (
          <Badge
            link={`/invoices/${getValue()._id}`}
            label={`Invoice ${getValue().invoiceId}`}
          ></Badge>
        );
      }
      return null;
    },
    visible: canEditJobs(),
  },
  {
    id: "note",
    minWidth: 140,
    cell: ({ getValue }) => {
      if (getValue()) {
        return (
          <Popover
            className="w-43"
            position="top"
            trigger={<Badge label={"Note"}></Badge>}
            offset={[-12, 0]}
          >
            {getValue()}
          </Popover>
        );
      }
    },
    remove: !canEditJobs(),
  },
  {
    id: "totalMileage",
    visible: false,
  },
  {
    id: "active",
    visible: false,
  },
  {
    id: "completed",
    visible: false,
  },
  {
    id: "author",
    visible: false,
    cell: ({ getValue }) => {
      return getValue()?.fullName;
    },
  },
  {
    id: "dateUpdated",
    visible: false,
    cell: ({ getValue }) => formatDate(getValue(), null, true),
    disableGlobalFilter: true,
  },
  {
    id: "dateCreated",
    visible: false,
    cell: ({ getValue }) => formatDate(getValue(), null, true),
    disableGlobalFilter: true,
  },
  {
    id: "_id",
    header: "id",
    visible: false,
    disableGlobalFilter: true,
  },
];

export const COLUMN_DEFINITIONS = renderCustomColumnDefs(tableConfig);
export const FORM_OPTIONS = {
  validations: renderValidations(tableConfig, "jobs"),
};
export const PAGE_SIZE_OPTIONS = [
  { label: "10 Jobs", value: 10 },
  { label: "20 Jobs", value: 20 },
  { label: "50 Jobs", value: 50 },
  { label: "100 Jobs", value: 100 },
  { label: "150 Jobs", value: 150 },
];
